// Approved legal copy — reviewed by Jessica and Dean. Content lives here, as
// data, deliberately separate from page layout (see LegalPage.jsx) so it can
// be swapped later (e.g. after attorney review) without touching markup.
//
// PRIVACY_MD/TERMS_MD are the approved-content rebuild (superseding the
// Phase 15 draft — see docs/ANALYSIS.md Phase 17). The only edits made to
// the copy as provided are: wrapping support@zynacular.com in markdown link
// syntax so it's clickable, and adding a "---" divider before the "Last
// updated" line so it renders as a muted footnote via the existing
// `.legal-prose hr + p` rule (legal.css) — the same structural device used
// previously, not a wording change. No words were changed, reordered, or
// removed. CONTACT_MD is untouched — this brief only replaced Privacy/Terms.

export const PRIVACY_MD = `# Privacy Policy

**Zynacular LLC**

Zynacular LLC ("Zynacular," "we," "us," or "our") provides a service that helps users create and customize job application and professional documents, including resumes, CVs, cover letters, LinkedIn summaries, professional bios, and portfolio content. To provide the service, we process personal information, including information about your employment history, experience, skills, and qualifications.

This Privacy Policy explains what information we collect, how we use and disclose it, how long we retain it, and the choices and rights available to you.

Zynacular operates from the United States. Your information may be processed and stored in the United States and in other locations where our service providers operate, subject to applicable law.

This Privacy Policy applies to personal information processed through Zynacular's website, applications, services, and related support interactions.

## Information We Collect

**Information You Provide.** We collect information you provide when you create or use your account, including your email address and account credentials. We also process resumes, CVs, job descriptions, employment information, professional history, skills, qualifications, company and job information, and other content you submit when using Zynacular.

We also collect information you provide when you communicate with us, including information submitted to [support@zynacular.com](mailto:support@zynacular.com).

**Information Collected Automatically.** When you access or use Zynacular, we may collect basic technical and device information necessary to operate and secure the service, such as your IP address, browser type, device information, and information related to your interaction with the service.

We also receive transaction and payment-related records from our payment processor when you purchase credits. Zynacular does not receive or store your full payment card number.

We do not currently use third-party advertising, behavioral advertising, or third-party analytics or tracking scripts. If our practices materially change, we will update this Privacy Policy and provide any notice or choices required by applicable law.

If Zynacular introduces analytics, advertising technologies, or other tracking technologies in the future, we will update this Privacy Policy and provide any consent mechanisms, notices, or opt-out choices required by applicable law before or when those technologies are implemented.

## Sources of Personal Information

We collect personal information primarily from the following sources:

- Directly from you when you create an account, provide a resume or other professional information, submit a job description or other content, purchase credits, use Zynacular, or contact us;
- Automatically from your browser or device when you access or use the service; and
- From service providers involved in transactions or operation of the service, such as our payment processor and other providers that support Zynacular.

We do not purchase personal information about our users from data brokers.

## Sensitive Personal Information

Zynacular does not require you to provide sensitive personal information that is unnecessary to create or customize your professional documents. Because resumes, CVs, job descriptions, and other materials are supplied by users, however, information you choose to submit may sometimes contain information that is considered sensitive under applicable privacy laws.

We encourage you not to include sensitive personal information that is unnecessary for your job application or use of Zynacular.

If sensitive personal information is included in materials you submit, we process it only as reasonably necessary to provide the service, fulfill your request, maintain security, comply with applicable law, or for other purposes permitted by applicable law. We do not use sensitive personal information for third-party advertising.

## How We Use Your Information

We use personal information as necessary to provide and operate Zynacular, including to:

- Generate and customize the documents you request;
- Create, maintain, and administer your account;
- Process and maintain records of purchases and credits;
- Provide customer support;
- Maintain the security and integrity of the service;
- Detect and prevent fraud, misuse, and unauthorized access;
- Troubleshoot and improve the functionality and performance of the service;
- Comply with applicable legal obligations; and
- Enforce our Terms of Service.

We do not use your submitted resumes, job descriptions, or generated documents to train AI models. We do not use your personal information for third-party advertising, and we do not sell your personal information.

When we improve Zynacular, we may use operational, technical, and service information that does not require using the content of your resume, job description, or generated documents to train AI models.

## Artificial Intelligence Processing

Zynacular uses third-party artificial intelligence services, including OpenAI, to provide document-generation and customization features.

When you request a generation, information necessary to complete that request — which may include your resume, CV, job description, professional information, instructions, and related content — is transmitted to the applicable AI provider for processing.

AI processing is necessary to provide Zynacular's AI-powered document-generation and customization features and cannot be disabled while using those features.

Zynacular does not use your submitted content to train AI models.

OpenAI states that, by default, inputs and outputs submitted through its API are not used to train or improve its models unless an organization affirmatively opts in to data sharing. Third-party AI providers process information in accordance with their applicable contractual terms, privacy commitments, security practices, and data-processing policies. Their practices and technologies may change over time.

If you do not want information contained in your resume, job description, or other submitted materials processed by a third-party AI provider, you should not submit that information to Zynacular or use the applicable AI-powered features.

## How We Share Your Information

We disclose personal information to service providers that help us operate Zynacular. These may include:

- Artificial intelligence providers;
- Payment processors;
- Hosting and infrastructure providers;
- Database providers;
- Email and communications providers; and
- Security and technical service providers.

These providers may access or process personal information only as necessary to provide their services to Zynacular and subject to applicable contractual and legal obligations.

Stripe processes payments made through Zynacular. Zynacular does not receive or store your full payment card number.

We may also disclose information when reasonably necessary to:

- Comply with applicable law, legal process, or lawful government requests;
- Protect the rights, property, security, or safety of Zynacular, our users, or others;
- Investigate or prevent fraud, abuse, unauthorized activity, or security incidents; or
- Facilitate a merger, acquisition, financing, corporate reorganization, sale of assets, or similar business transaction.

We do not sell your personal information.

If Zynacular is involved in a merger, acquisition, financing, reorganization, sale of assets, or similar transaction, personal information may be transferred as part of that transaction, subject to applicable law and any required notices or protections.

## Cookies and Browser Storage

Zynacular uses browser-based storage and similar technologies that are necessary to operate the service, maintain your login session, and preserve certain in-progress application information.

Drafts are stored on your device, do not include your resume text, are associated with your account, are cleared when you sign out, and expire after seven days.

Zynacular may use cookies or similar browser technologies that are necessary for authentication, security, account functionality, and operation of the service.

We do not currently use cookies or similar technologies for third-party advertising or behavioral tracking.

## Online Tracking and Privacy Preference Signals

Zynacular does not currently engage in third-party behavioral advertising or track users across unaffiliated websites or online services for advertising purposes. Because we do not currently engage in those practices, a browser's Do Not Track signal does not change how Zynacular processes information.

Where applicable law requires recognition of a legally valid browser-based opt-out preference signal, such as Global Privacy Control ("GPC"), Zynacular will honor the signal as required by applicable law.

We do not currently permit third parties to collect personal information through Zynacular for their own cross-context behavioral advertising or targeted advertising purposes. Service providers may process information as described in this Privacy Policy solely in connection with providing services to Zynacular and subject to applicable contractual and legal obligations.

## Data Retention and Deletion

We retain personal information for as long as reasonably necessary to provide Zynacular, maintain your account and saved content, fulfill the purposes described in this Privacy Policy, comply with legal and financial obligations, resolve disputes, maintain security, prevent fraud and abuse, and enforce our agreements.

While your account remains open, saved resumes, documents, and related account information may remain available so you can access them when you return.

You may delete your saved content at any time through the service.

To request deletion of your account, contact [support@zynacular.com](mailto:support@zynacular.com). We may take reasonable steps to verify your identity before processing a deletion request and will confirm when the request has been completed.

Following deletion, we will delete or de-identify personal information within a reasonable period, except where information must or may be retained for legitimate legal, security, fraud-prevention, accounting, tax, dispute-resolution, or other lawful purposes.

Information may remain temporarily in backup or disaster-recovery systems until those systems are overwritten or deleted in the ordinary course of business. We may also retain limited information where reasonably necessary to comply with law, maintain financial or transaction records, prevent fraud or abuse, resolve disputes, enforce agreements, or establish, exercise, or defend legal claims.

Unused credits are forfeited when you delete your account, except where applicable law requires otherwise.

## Your Privacy Rights

Depending on where you live and subject to applicable law, you may have certain rights concerning your personal information.

These rights may include the right to:

- Request access to or confirmation of personal information we process about you;
- Correct inaccurate personal information;
- Request deletion of personal information;
- Obtain a copy of certain personal information; and
- Opt out of certain types of processing where applicable.

You may access and correct certain account information by signing in and may delete saved content through the service. You may also contact us to request deletion of your account.

Residents of Colorado, California, and certain other states may have additional privacy rights under applicable state law.

To exercise a privacy right, contact [support@zynacular.com](mailto:support@zynacular.com). We may need to verify your identity before completing certain requests.

You will not be discriminated against for exercising privacy rights provided by applicable law.

Where required by applicable law, you may designate an authorized agent to submit certain privacy requests on your behalf. We may take reasonable steps permitted by law to verify the agent's authority and your identity before processing the request.

If applicable law gives you the right to appeal our decision concerning a privacy request, you may submit an appeal by contacting [support@zynacular.com](mailto:support@zynacular.com) and identifying the request you want us to reconsider.

Zynacular also offers reasonable access, correction, and deletion options to users regardless of where they live, subject to legitimate legal, security, fraud-prevention, and record-retention requirements.

## Age Requirements

Zynacular is intended for users who are at least 16 years old. If applicable law requires a higher minimum age to use the service, that higher age requirement applies.

If you are under the age of majority where you live, your parent or legal guardian must review and agree to our Terms of Service on your behalf.

We do not knowingly collect personal information from anyone below the applicable minimum age.

If you believe that a person below the applicable minimum age has provided personal information to Zynacular, contact [support@zynacular.com](mailto:support@zynacular.com) so we can review the situation and take appropriate action, including deletion where appropriate.

## Data Security

We use reasonable administrative, technical, and organizational safeguards designed to protect personal information.

Our security safeguards include encrypted connections, hashed passwords, and access controls designed to limit unauthorized access to personal information.

No method of electronic transmission, storage, or security is completely secure, and we cannot guarantee absolute security.

If we become aware of a security incident involving personal information, we will investigate the incident and provide notices to affected individuals, regulators, or others where required by applicable law.

## Updates to This Privacy Policy

We may update this Privacy Policy from time to time to reflect changes to Zynacular, our privacy practices, technology, service providers, or applicable law.

If we make material changes, we will update the "Last updated" date and provide reasonable notice through the service, by email, or by another appropriate method where required by applicable law.

We encourage you to review this Privacy Policy periodically so you remain informed about how Zynacular handles personal information.

## Contact Us

Questions about this Privacy Policy, our privacy practices, or requests concerning your personal information may be sent to:

**[support@zynacular.com](mailto:support@zynacular.com)**

When you contact us about a privacy request, we may ask for information reasonably necessary to verify your identity, locate the relevant information, and protect your account from unauthorized requests.

We aim to respond to inquiries within two business days.

Privacy rights requests will be processed within the time periods required by applicable law.

---

*Last updated August 20, 2026*
`;

export const TERMS_MD = `# Terms of Service

**Zynacular LLC**

These Terms of Service ("Terms") are an agreement between you and Zynacular LLC, a Colorado limited liability company ("Zynacular," "we," "us," or "our"). By creating an account, accessing, or using Zynacular, you agree to these Terms. If you do not agree to these Terms, please do not use the service.

## Eligibility

You must be at least 16 years old to use Zynacular. If the law where you live requires you to be older to use the service or enter into these Terms, that higher minimum age applies. If you are under the age of majority where you live, your parent or legal guardian must review and agree to these Terms on your behalf.

## About the Service

We use artificial intelligence ("AI") to create and customize job application and professional documents — including resumes, CVs, cover letters, LinkedIn summaries, professional bios, and portfolio content — based on the information and materials you provide.

Zynacular is a document-generation and customization service. We do not provide career counseling, legal advice, recruiting services, or job placement, and we do not guarantee interviews, offers, employment, or any other employment outcome.

Zynacular may update, modify, add, or discontinue features of the service from time to time as our technology, AI capabilities, third-party services, and product offerings evolve. This may include changes to available AI models, document-generation features, scoring or analysis methods, and other functionality.

## Your Account

Keep your login credentials secure and do not share them with others. You are responsible for activity that occurs through your account. If you believe your account or login credentials have been compromised, contact [support@zynacular.com](mailto:support@zynacular.com) promptly.

Your account is personal to you and may not be sold, transferred, or assigned to another person without our permission. Credits associated with your account may not be sold, transferred, exchanged for cash, or used outside Zynacular unless we expressly permit it.

## Credits and Payment

One credit covers one complete application package for one job — including all documents you select as part of that single generation.

Credits are sold in packages at the prices displayed on Zynacular at the time of purchase. Credits do not expire while your account remains open. If we change our credit-expiration policy in the future, the change will apply only to credits purchased after the new policy takes effect. Credits you already hold will remain subject to the terms in effect when they were purchased.

Promotional, referral, complimentary, trial, bonus, or other credits that are not purchased may be subject to separate terms, eligibility requirements, restrictions, or expiration periods disclosed when those credits are issued. These terms do not change the expiration terms applicable to credits you purchase.

Payments are processed by Stripe. Zynacular does not receive or store your full payment card number.

Prices and any applicable taxes will be displayed as part of the purchase process. You are responsible for applicable taxes associated with your purchase, except for taxes imposed on Zynacular's income.

## Refunds

Credits are non-refundable once used, except where a refund is required by applicable law.

If a generation fails because of a technical problem with Zynacular, contact [support@zynacular.com](mailto:support@zynacular.com). Our standard remedy is to restore the affected credit so you can generate the application package again. We may provide a different remedy when appropriate or when required by law.

We do not provide refunds based solely on dissatisfaction with the wording, style, recommendations, or other subjective aspects of generated content, or because an application does not result in an interview, offer, or other desired employment outcome. Generated content depends substantially on the information and materials you provide.

If you choose to delete your account, any unused credits remaining in the account are forfeited, except where applicable law requires otherwise.

## Your Content and Your Documents

You retain ownership of the content and materials you submit to Zynacular. To the extent permitted by applicable law, you also own the documents generated for you through the service and may use them for your personal and professional purposes.

You give us permission to process your content only as necessary to provide the service and generate your requested documents. We do not use your content to train AI models. Our handling and retention of your information is also governed by our Privacy Policy.

You are responsible for making sure you have the right to submit the information and materials you provide to us — and for reviewing all generated content before submitting or publishing it.

AI-generated content can contain errors, omissions, or inaccurate information. You are responsible for ensuring that anything you submit to an employer or other third party is accurate, truthful, and appropriately represents your experience and qualifications.

## Limitations and Disclaimers

We cannot guarantee that using Zynacular will result in an interview, job offer, employment, recruiter response, or any other particular outcome.

We also cannot guarantee that your documents will pass, achieve a particular score in, or receive favorable treatment from any applicant tracking system ("ATS").

We design our output using recognized formatting, keyword, and document-optimization practices intended to work effectively with modern ATS software. However, applicant tracking systems are operated by third parties. We do not control those systems, know all of their evaluation methods, or control changes to their technology or processes.

Any ATS score or analysis displayed by Zynacular is based on Zynacular's own methodology. It is an informational assessment and is not the score or evaluation that a particular employer, recruiter, or applicant tracking system will necessarily assign to your documents.

To the fullest extent permitted by applicable law, Zynacular is provided "as is" and "as available," without warranties of any kind, whether express or implied.

## Rules

You may not:

- Submit content you do not have the right to use;
- Create false, misleading, or fraudulent employment documents;
- Attempt to gain unauthorized access to Zynacular, another user's account, or related systems;
- Reverse engineer, scrape, copy, or interfere with the service except where such restrictions are prohibited by law;
- Resell or commercially redistribute access to the service without our written permission; or
- Use Zynacular for unlawful purposes.

If we suspend or close your account because of a material or intentional violation of these Terms, unused credits may be forfeited, subject to applicable law. We may use reasonable judgment where a violation was inadvertent or resulted from an account compromise.

If we close your account for reasons unrelated to your conduct — including if we discontinue the service — we will provide an appropriate remedy for unused credits, which may include continued access, replacement, a refund, or another appropriate remedy.

## Intellectual Property

Zynacular and its proprietary technology and materials — including its software, design, workflows, prompts, systems, branding, and other proprietary elements — are owned by Zynacular LLC and are protected by applicable intellectual property laws.

Using the service does not transfer ownership of those materials or intellectual property rights to you.

Your ownership rights in the content you provide and documents generated for you are described in the "Your Content and Your Documents" section above.

Zynacular's software platform, technology, systems, and related future developments are patent pending.

Subject to these Terms, Zynacular grants you a limited, personal, non-exclusive, non-transferable, and revocable right to access and use the service for your own lawful personal and professional purposes. This right does not give you ownership of Zynacular or any of its software, technology, systems, prompts, workflows, branding, or other intellectual property.

If you voluntarily provide feedback, suggestions, ideas, or recommendations about Zynacular, you agree that we may use that feedback to evaluate, develop, and improve the service without restriction or compensation to you. This does not give Zynacular ownership of your resumes, documents, or other user content.

## Third-Party Services and Service Availability

Zynacular relies on third-party providers for certain parts of the service, including AI, payment processing, hosting, infrastructure, and other technology services. Those third-party services may experience outages, interruptions, changes, or other events outside our reasonable control.

We are not responsible for failures caused by circumstances outside our reasonable control, but we will make reasonable efforts to maintain and restore the availability of Zynacular when disruptions occur.

## Electronic Communications

By creating an account or using Zynacular, you agree that we may provide account-related, transactional, administrative, security, and legal communications to you electronically, including by email or through the service. You are responsible for keeping the email address associated with your account current.

Where applicable law requires separate consent for promotional or marketing communications, those communications will be handled separately and are not required as a condition of using Zynacular unless otherwise permitted by law.

## Liability

To the fullest extent permitted by applicable law, Zynacular LLC and its owners, officers, employees, contractors, and affiliates will not be liable for indirect, incidental, special, consequential, exemplary, or punitive damages, including lost employment opportunities, lost profits, lost data, or similar losses arising out of or related to your use of the service.

To the fullest extent permitted by applicable law, our total aggregate liability for claims arising out of or relating to Zynacular or these Terms will not exceed the amount you paid to Zynacular during the twelve months immediately preceding the event giving rise to the claim.

To the extent permitted by applicable law, you agree to indemnify and hold Zynacular LLC harmless from third-party claims, liabilities, damages, and reasonable expenses arising from content you submit, your unlawful or unauthorized use of the service, or your material violation of these Terms.

## Account Suspension and Termination

You may stop using Zynacular at any time. To request deletion of your account, contact [support@zynacular.com](mailto:support@zynacular.com). Account deletion and the handling of information associated with your account are subject to our Privacy Policy and any retention required or permitted by applicable law.

Before deleting your account, you should download or otherwise save any generated documents you wish to retain. After your account is deleted, you may no longer be able to access documents or other content previously stored through the service. Account deletion does not affect your ownership of copies of generated documents you previously downloaded or saved.

We may suspend or terminate access to the service if you materially violate these Terms, if suspension or termination is required by law, to protect Zynacular or its users from fraud, abuse, or security threats, or if we discontinue the service.

The treatment of unused credits following suspension or termination is described in the "Rules" and "Credits and Payment" sections above.

## Disputes

Before beginning formal proceedings, you and Zynacular agree to make a good-faith effort to resolve the dispute informally. You may begin that process by contacting [support@zynacular.com](mailto:support@zynacular.com).

If a dispute cannot be resolved informally, it will be resolved by binding individual arbitration, except that either party may bring an eligible claim in small claims court. Disputes will be brought individually, and not as part of a class, collective, consolidated, or representative proceeding, to the extent permitted by applicable law.

These Terms are governed by the laws of the State of Colorado, without regard to its conflict of laws rules.

## Updates and Amendments

We may update these Terms from time to time. If we make material changes, we will update the "Last updated" date and provide reasonable notice through the service, by email, or by another appropriate method.

Where required by law, we will obtain your consent before material changes take effect.

If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions will remain in effect to the fullest extent permitted by law.

These Terms, together with our Privacy Policy and any other terms or policies expressly incorporated into them, constitute the agreement between you and Zynacular concerning your use of the service.

## Additional Legal Terms

You may not assign or transfer these Terms or your rights or obligations under them without our prior written consent. Zynacular may assign or transfer these Terms in connection with a merger, acquisition, corporate reorganization, sale of assets, or similar business transaction, or as otherwise permitted by law.

If Zynacular does not enforce a provision of these Terms in a particular instance, that does not waive our right to enforce that provision or any other provision in the future.

Any provisions of these Terms that by their nature should continue after your account is closed or these Terms end will remain in effect. This includes provisions concerning intellectual property, disclaimers, limitations of liability, indemnification, dispute resolution, and any obligations that arose before termination.

Section headings are provided for convenience and readability only and do not affect the meaning or interpretation of these Terms.

## Contact Us

Questions about these Terms or Zynacular may be sent to:

**[support@zynacular.com](mailto:support@zynacular.com)**

We aim to respond to support inquiries within two business days.

---

*Last updated August 20, 2026*
`;

export const CONTACT_MD = `# Contact

## Get in touch

Questions, problems, or feedback — email us at **[support@zynacular.com](mailto:support@zynacular.com)**. We aim to respond within 2 business days.

Please don't send passwords or payment card details by email. We will never ask you for either.

## Privacy and data requests

To access, correct, or delete your personal information, or to delete your account, email **[support@zynacular.com](mailto:support@zynacular.com)**.

See our [Privacy Policy](/privacy) for details on what we collect and how we handle it.
`;
